import React, { useContext, useEffect, useRef } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import noop from 'lodash/noop';
import styled from 'styled-components';

import ModifierForm from './ModifierForm';
import ModuleForm from './ModuleForm';
import ThreadModuleProvider, { ThreadModuleContext } from './ThreadModuleContext';
import Form, { useForm } from 'src/presentation/components/forms/Form';
import useOnBlur from './hooks/useOnBlur';
import useOnSave from './hooks/useOnSave';

export default function SidePanelModule({
  readOnly,
  isAnonymousModule,
}: {
  readOnly: boolean;
  isAnonymousModule: boolean;
}) {
  return (
    <ThreadModuleProvider readOnly={readOnly} isAnonymousModule={isAnonymousModule}>
      <StyledThreadModuleWrapper>
        <ModalOrModifierForm />
      </StyledThreadModuleWrapper>
    </ThreadModuleProvider>
  );
}

export const ModalOrModifierForm = () => {
  const { isModifier, module, modifier } = useContext(ThreadModuleContext);
  const modifierOrModule = isModifier ? modifier : module;

  return (
    <SidekickFormProvider key={modifierOrModule?.id} modifierOrModule={modifierOrModule}>
      {isModifier ? <ModifierForm /> : <ModuleForm />}
    </SidekickFormProvider>
  );
};

const SidekickFormProvider = ({ children, modifierOrModule }) => {
  const setNeedSaveRef = useRef(false);
  const priorItemRef = useRef(cloneDeep(modifierOrModule));

  const blurHandler = useOnBlur({ setNeedSaveRef });
  const saveHandler = useOnSave();

  const formContext = useForm({
    item: modifierOrModule,
    resetErrors: noop,
    onSave: saveHandler,
    onBlur: blurHandler,
  });

  useEffect(() => {
    return () => {
      if (setNeedSaveRef.current) {
        setNeedSaveRef.current = false;
        formContext.onSave();
        priorItemRef.current = cloneDeep(formContext.workingState);
      }
    };
  });

  useEffect(() => {
    if (setNeedSaveRef.current) {
      setNeedSaveRef.current = false;
      formContext.onSave();
      priorItemRef.current = cloneDeep(formContext.workingState);
    }
  }, [setNeedSaveRef, formContext, priorItemRef]);

  return <Form context={formContext}>{children}</Form>;
};

const StyledThreadModuleWrapper = styled.div`
  padding: 32px 24px;
`;
